/**
 * v2 Settings → Templates. List + detail (version history /
 * rollback). v1's editor lifts cleanly because the CLI body is just
 * a textarea; we wrap it in v2 chrome.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, History, Pencil, RotateCcw, Save, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { api } from "../../../api/client";
import { LocalTime } from "../../../lib/datetime";
import { Badge } from "../../components/Badge";
import { CopyableCell } from "../../components/CopyableCell";
import { useToast } from "../../components/Toast";
import { HelpButton } from "../../help/HelpPanel";

type Template = {
  id: number;
  adapter_key: string;
  template_name: string;
  active_version_id: number | null;
  active_version_number: number | null;
  description: string | null;
};
type TemplateListResponse = { templates: Template[] };

export function Templates() {
  const q = useQuery<TemplateListResponse>({
    queryKey: ["v2", "templates"],
    queryFn: async () => {
      const res = await api.get<TemplateListResponse>("/templates");
      return res.data;
    },
  });
  return (
    <section className="flex flex-col gap-3">
      <header>
        <div className="flex items-center gap-2">
          <h1 className="text-lg font-semibold text-slate-100">Templates</h1>
          <HelpButton topic="settings-templates" />
        </div>
        <p className="text-xs text-slate-500">
          Per-vendor command templates with version history. Edits
          create a new version; rollback restores any prior version
          as active.
        </p>
      </header>
      <div className="rounded-lg bg-slate-900 shadow-border p-4">
        {q.isLoading ? (
          <p className="text-xs text-slate-500">Loading…</p>
        ) : (q.data?.templates.length ?? 0) === 0 ? (
          <p className="text-xs text-slate-500">No templates registered.</p>
        ) : (
          <table className="w-full text-xs">
            <thead className="text-left text-[10px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-2 py-1">Adapter</th>
                <th className="px-2 py-1">Template</th>
                <th className="px-2 py-1">Active version</th>
                <th className="px-2 py-1">Description</th>
                <th className="px-2 py-1 text-right">Open</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/40 text-slate-300">
              {(q.data?.templates ?? []).map((t) => (
                <tr key={t.id}>
                  <td className="px-2 py-1.5">
                    <Badge tone="info">{t.adapter_key}</Badge>
                  </td>
                  <td className="px-2 py-1.5 font-mono">
                    <CopyableCell value={t.template_name} />
                  </td>
                  <td className="px-2 py-1.5 font-mono">
                    v{t.active_version_number ?? "?"}
                  </td>
                  <td className="px-2 py-1.5 text-slate-400">
                    {t.description ?? "—"}
                  </td>
                  <td className="px-2 py-1.5 text-right">
                    <Link
                      to={`/settings/templates/${t.id}`}
                      className="text-emerald-400 hover:text-emerald-300"
                    >
                      open
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

// Backend GET /templates/{id} returns a FLAT TemplateDetail (the metadata
// + the active version inlined as ``active_version``). Version history is a
// separate endpoint, GET /templates/{id}/versions, returning summary rows
// (no body). See apps/web/app/schemas/templates.py.
type VersionDetail = {
  id: number;
  template_id: number;
  version_number: number;
  body: string;
  ignore_patterns: string[];
  source: string;
  note: string | null;
  created_at: string;
  created_by_gigafy_username: string;
  is_active: boolean;
};
type Detail = {
  id: number;
  adapter_key: string;
  name: string;
  description: string | null;
  scope: string;
  destructive: boolean;
  active_version: VersionDetail | null;
};
type VersionRow = {
  id: number;
  version_number: number;
  source: string;
  note: string | null;
  created_at: string;
  created_by_gigafy_username: string;
  is_active: boolean;
};
type VersionsResponse = {
  template_id: number;
  active_version_id: number | null;
  versions: VersionRow[];
};

export function TemplateDetail({ templateId }: { templateId: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [body, setBody] = useState("");
  const [ignoreCsv, setIgnoreCsv] = useState("");
  const [note, setNote] = useState("");

  const q = useQuery<Detail>({
    queryKey: ["v2", "templates", templateId],
    queryFn: async () => {
      const res = await api.get<Detail>(`/templates/${templateId}`);
      return res.data;
    },
  });

  const versionsQ = useQuery<VersionsResponse>({
    queryKey: ["v2", "templates", templateId, "versions"],
    queryFn: async () => {
      const res = await api.get<VersionsResponse>(
        `/templates/${templateId}/versions`,
      );
      return res.data;
    },
  });

  const active = q.data?.active_version ?? null;

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["v2", "templates", templateId] });
    qc.invalidateQueries({
      queryKey: ["v2", "templates", templateId, "versions"],
    });
  };

  // Seed the editor with the active version's body whenever the
  // operator opens it (or the active version reloads after a save).
  useEffect(() => {
    if (active && editing) {
      setBody(active.body);
      setIgnoreCsv((active.ignore_patterns ?? []).join(", "));
      setNote("");
    }
  }, [active, editing]);

  const saveNew = useMutation({
    mutationFn: async () => {
      const patterns = ignoreCsv
        .split(",")
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
      const res = await api.post(`/templates/${templateId}/versions`, {
        body,
        ignore_patterns: patterns,
        note: note.trim() || null,
      });
      return res.data;
    },
    onSuccess: () => {
      toast.push("New version saved + activated", "success");
      invalidate();
      setEditing(false);
    },
    onError: () => toast.push("Save failed — body may be invalid", "error"),
  });

  const rollback = useMutation({
    mutationFn: async (versionId: number) => {
      const res = await api.post(`/templates/${templateId}/rollback`, {
        version_id: versionId,
      });
      return res.data;
    },
    onSuccess: () => {
      toast.push("Rolled back — new active version created", "success");
      invalidate();
    },
    onError: () => toast.push("Rollback failed", "error"),
  });

  if (q.isError) {
    return (
      <section className="flex flex-col gap-3">
        <Link
          to="/settings/templates"
          className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-slate-200"
        >
          <ChevronLeft className="h-3 w-3" />
          Back to templates
        </Link>
        <p className="text-xs text-red-400">
          Failed to load this template. It may not exist — go back and pick
          another.
        </p>
      </section>
    );
  }

  return (
    <section className="flex flex-col gap-3">
      <Link
        to="/settings/templates"
        className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-slate-200"
      >
        <ChevronLeft className="h-3 w-3" />
        Back to templates
      </Link>
      <header className="flex flex-wrap items-baseline gap-3">
        <h1 className="text-lg font-semibold text-slate-100">
          {q.data?.name ?? "template"}
        </h1>
        <HelpButton topic="settings-templates" />
        <Badge tone="info">{q.data?.adapter_key}</Badge>
        {active && !editing && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="ml-auto inline-flex items-center gap-1 rounded border border-slate-700 bg-slate-800 px-2 py-1 text-xs text-slate-200 hover:bg-slate-700"
          >
            <Pencil className="h-3 w-3" />
            Edit active version
          </button>
        )}
      </header>

      {active && !editing && (
        <section className="rounded-lg bg-slate-900 shadow-border p-4">
          <h2 className="mb-2 text-sm font-medium text-slate-200">
            Active version (v{active.version_number}) ·{" "}
            <span className="text-xs text-slate-500">
              by {active.created_by_gigafy_username} at{" "}
              <LocalTime iso={active.created_at} />
            </span>
            {active.note && (
              <span className="ml-2 text-xs italic text-slate-500">
                — {active.note}
              </span>
            )}
          </h2>
          <pre className="overflow-x-auto rounded bg-slate-950 p-2 font-mono text-[11px] text-slate-300">
            {active.body}
          </pre>
          {active.ignore_patterns?.length > 0 && (
            <div className="mt-2 text-[10px] text-slate-500">
              ignore_patterns:{" "}
              <span className="font-mono text-slate-400">
                {active.ignore_patterns.join(", ")}
              </span>
            </div>
          )}
        </section>
      )}

      {editing && active && (
        <section className="rounded-lg border border-emerald-800 bg-slate-900 p-4">
          <h2 className="mb-2 text-sm font-medium text-emerald-200">
            New version (saving creates v{active.version_number + 1})
          </h2>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            spellCheck={false}
            className="h-72 w-full rounded border border-slate-700 bg-slate-950 p-2 font-mono text-[11px] text-slate-100 focus-visible:border-emerald-700 focus-visible:outline-none"
          />
          <div className="mt-2 grid gap-2 sm:grid-cols-2">
            <label className="flex flex-col gap-1 text-xs">
              <span className="text-slate-400">
                ignore_patterns (comma-separated regex)
              </span>
              <input
                type="text"
                value={ignoreCsv}
                onChange={(e) => setIgnoreCsv(e.target.value)}
                className="rounded border border-slate-700 bg-slate-950 px-2 py-1 font-mono text-sm text-slate-100 focus-visible:border-emerald-700 focus-visible:outline-none"
              />
            </label>
            <label className="flex flex-col gap-1 text-xs">
              <span className="text-slate-400">
                note (shown in version history)
              </span>
              <input
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. fix typo in vlan binding"
                className="rounded border border-slate-700 bg-slate-950 px-2 py-1 text-sm text-slate-100 focus-visible:border-emerald-700 focus-visible:outline-none"
              />
            </label>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              onClick={() => saveNew.mutate()}
              disabled={saveNew.isPending || body.trim() === ""}
              className="inline-flex items-center gap-1 rounded border border-emerald-700 bg-emerald-900/40 px-3 py-1.5 text-xs text-emerald-200 hover:bg-emerald-900/60 disabled:opacity-50"
            >
              <Save className="h-3 w-3" />
              {saveNew.isPending ? "Saving…" : "Save new version"}
            </button>
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="inline-flex items-center gap-1 rounded border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:bg-slate-800"
            >
              <X className="h-3 w-3" />
              Cancel
            </button>
            <span className="ml-2 text-[10px] text-slate-500">
              The new version becomes active immediately; the current
              v{active.version_number} stays in the history below.
            </span>
          </div>
        </section>
      )}

      <section className="rounded-lg bg-slate-900 shadow-border p-4">
        <h2 className="mb-2 inline-flex items-center gap-1 text-sm font-medium text-slate-200">
          <History className="h-3.5 w-3.5" />
          Version history ({versionsQ.data?.versions.length ?? 0})
        </h2>
        {versionsQ.isLoading ? (
          <p className="text-xs text-slate-500">Loading…</p>
        ) : versionsQ.isError ? (
          <p className="text-xs text-red-400">Failed to load version history.</p>
        ) : (
          <ul className="flex flex-col gap-1 text-xs">
            {(versionsQ.data?.versions ?? [])
              .filter((v) => !v.is_active)
              .sort((a, b) => b.version_number - a.version_number)
              .map((v) => (
                <li
                  key={v.id}
                  className="flex items-baseline gap-2 rounded border border-slate-800 px-2 py-1.5"
                >
                  <span className="font-mono">v{v.version_number}</span>
                  <span className="text-slate-400">
                    {v.created_by_gigafy_username}
                  </span>
                  <span className="text-slate-500">
                    <LocalTime iso={v.created_at} />
                  </span>
                  {v.source && (
                    <Badge
                      tone={v.source === "rollback" ? "warning" : "muted"}
                    >
                      {v.source}
                    </Badge>
                  )}
                  {v.note && (
                    <span className="text-[10px] italic text-slate-500">
                      — {v.note}
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      if (
                        window.confirm(
                          `Roll back to v${v.version_number}? A new active version is created (history preserved).`,
                        )
                      ) {
                        rollback.mutate(v.id);
                      }
                    }}
                    disabled={rollback.isPending}
                    className="ml-auto inline-flex items-center gap-1 text-amber-400 hover:text-amber-300 disabled:opacity-50"
                    title="Restore this version as the active one"
                  >
                    <RotateCcw className="h-3 w-3" />
                    rollback
                  </button>
                </li>
              ))}
          </ul>
        )}
      </section>
    </section>
  );
}
